import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, ACCESS_LEVELS } from '@rti/shared';
import { trackGoogleAnalyticsEvent } from '@/utils/googleAnalytics';
import TermsConditions from '@/components/TermsConditions';
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter';
import RoleFields from '@/components/RoleFields';
import { Mail, Building2, ArrowRight } from 'lucide-react';

declare global {
  interface Window { grecaptcha?: any }
}

type Step = 'email' | 'signup' | 'verification';

const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const showSupportLink = ((import.meta as any).env?.VITE_SHOW_SUPPORT_LINK as string | undefined) === 'true';
  const recaptchaSiteKey = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY as string | undefined;

  // Domain check state
  const [emailInput, setEmailInput] = useState('');
  const [domainCheck, setDomainCheck] = useState<{ eligible: boolean; company_name?: string | null; reason?: string } | null>(null);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [freeEmailDomains, setFreeEmailDomains] = useState<Set<string>>(new Set());

  // Whether this signup joins an existing company (no trial) or is a new user (trial)
  const [joinsExistingCompany, setJoinsExistingCompany] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: '',
    company_name: '',
    buying_role: '',
    job_title: '',
  });

  // Separate state for "Other" buying role text input
  const [buyingRoleOther, setBuyingRoleOther] = useState('');

  // Load free email domains
  useEffect(() => {
    (async () => {
      try {
        const domains = await apiClient.getFreeEmailDomains();
        if (Array.isArray(domains) && domains.length > 0) {
          setFreeEmailDomains(new Set(domains.map((d) => String(d).toLowerCase())));
        }
      } catch {
        setFreeEmailDomains(new Set(['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com']));
      }
    })();
  }, []);

  // reCAPTCHA script
  useEffect(() => {
    if (!recaptchaSiteKey) return;
    if (window.grecaptcha) return;
    const script = document.createElement('script');
    script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [recaptchaSiteKey]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const isFreeEmailDomain = (email: string): boolean => {
    const domain = (email || '').toLowerCase().split('@')[1];
    if (!domain) return false;
    return freeEmailDomains.has(domain);
  };

  const deriveCompanyFromEmail = (email: string): string => {
    const domain = (email || '').toLowerCase().split('@')[1];
    if (!domain) return '';
    const base = domain.split('.')[0];
    if (!base) return '';
    return base.charAt(0).toUpperCase() + base.slice(1);
  };

  const getRecaptchaToken = async (): Promise<string | undefined> => {
    if (!recaptchaSiteKey) return undefined;
    const waitForGrecaptcha = async (timeoutMs = 5000): Promise<any> => {
      const start = Date.now();
      while (!window.grecaptcha) {
        if (Date.now() - start > timeoutMs) return undefined;
        await new Promise((r) => setTimeout(r, 100));
      }
      return window.grecaptcha;
    };
    try {
      const grecaptcha = await waitForGrecaptcha();
      if (!grecaptcha) return undefined;
      await new Promise<void>((resolve) => {
        try { grecaptcha.ready(() => resolve()); } catch { resolve(); }
      });
      const token = await grecaptcha.execute(recaptchaSiteKey, { action: 'signup' });
      return typeof token === 'string' && token.length > 0 ? token : undefined;
    } catch {
      return undefined;
    }
  };

  // Step 1: Check email domain
  const handleEmailCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const email = emailInput.trim().toLowerCase();

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (isFreeEmailDomain(email)) {
      setError('Please use a company email address. Personal email domains (Gmail, Yahoo, etc.) are not accepted.');
      return;
    }

    setCheckingDomain(true);
    try {
      const result = await apiClient.checkDomainEligibility(email);
      setDomainCheck(result);

      if (!result.eligible) {
        setError(result.reason || 'This email domain is not eligible.');
        return;
      }

      // Domain is eligible — move to signup form
      const joining = !!result.company_name;
      setForm(prev => ({
        ...prev,
        email,
        company_name: joining ? '' : (prev.company_name || deriveCompanyFromEmail(email)),
      }));
      setJoinsExistingCompany(joining);
      setStep('signup');
    } catch {
      setError('Unable to verify email domain. Please try again.');
    } finally {
      setCheckingDomain(false);
    }
  };

  // Password match check
  const passwordsMismatch = useMemo(() => {
    return (
      (form.password?.length || 0) > 0 &&
      (form.password_confirm?.length || 0) > 0 &&
      form.password !== form.password_confirm
    );
  }, [form.password, form.password_confirm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setError('');
  };

  // Step 2: Submit signup
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.first_name || !form.last_name || !form.password || !form.password_confirm) {
      setError('Please fill in all required fields');
      return;
    }
    if (!joinsExistingCompany && !form.company_name.trim()) {
      setError('Please enter your company name');
      return;
    }
    if (!form.buying_role) {
      setError('Please select your role');
      return;
    }
    if (form.buying_role === 'Other' && !buyingRoleOther.trim()) {
      setError('Please describe your role');
      return;
    }
    if (form.password !== form.password_confirm) {
      setError('Passwords do not match');
      return;
    }
    if (!agreedToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      const resp = await apiClient.signupFreeTrial({
        user: {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          password: form.password,
          password_confirm: form.password_confirm,
          company_name: form.company_name || undefined,
          buying_role: form.buying_role === 'Other' ? buyingRoleOther.trim() : form.buying_role,
          job_title: form.job_title.trim() || undefined,
          membership_id: ACCESS_LEVELS.BASE,
          free_trial_duration_days: joinsExistingCompany ? 0 : 60,
          skip_free_trial: joinsExistingCompany,
        } as any,
        recaptchaToken,
      });

      if (resp && (resp as any).success) {
        trackGoogleAnalyticsEvent('sign_up', { method: 'create_account' });
        setStep('verification');
      } else {
        setError('Signup failed');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    try {
      await apiClient.resendVerification(form.email);
      setResendCountdown(60);
      setError('Verification email sent! Please check your inbox and spam folder.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verification pending screen
  if (step === 'verification') {
    return (
      <div className="root">
        <div className="auth-root flex w-full h-[100vh]">
          <div className="flex flex-col justify-between items-center h-full w-full max-w-2xl px-8 py-10 bg-rs-black text-rs-off-white mx-auto">
            <a href="/">
              <img src="/logos/Primary/White.svg" alt="Resin Smart Logo" className="w-sm mb-8" />
            </a>
            <div>
              <div className="mb-4">
                <Mail className={'text-rs-dark-green mx-auto mb-4'} size={85} />
              </div>
              <h1 className="text-3xl font-light mb-8">Verify Your Email Address</h1>
            </div>
            <div className="text-center max-w-lg">
              <div className="mb-4">
                <p className="text-2xl text-rs-dark-green mb-2">We've sent a verification email to:</p>
                <p className="text-xl text-rs-off-white mb-10 pb-5">{form.email}</p>
                <div className="bg-rs-jet border border-rs-jet rounded-lg p-6 mb-6 text-left">
                  <h2 className="text-lg font-semibold mb-3 text-rs-green">Next Steps:</h2>
                  <ol className="flex flex-col gap-2 text-rs-off-white/90">
                    <li className="flex items-start"><span className="text-rs-green mr-2">1.</span><span>Check your email inbox for a message from ResinSmart</span></li>
                    <li className="flex items-start"><span className="text-rs-green mr-2">2.</span><span>Click the verification link in the email</span></li>
                    <li className="flex items-start"><span className="text-rs-green mr-2">3.</span><span>You'll be automatically logged in and redirected to your dashboard</span></li>
                  </ol>
                </div>
                <div className="bg-rs-dark-green rounded-lg p-4 mb-6">
                  <p className="text-rs-off-black">
                    <strong>Can't find the email?</strong><br />The email should arrive within a few minutes.<br />Check your spam or junk folder.
                  </p>
                </div>
                {error && (
                  <div className={`text-sm mb-4 ${error.includes('sent') ? 'text-rs-green' : 'text-rs-red'}`}>{error}</div>
                )}
                <button onClick={handleResendVerification} disabled={loading || resendCountdown > 0} className="text-rs-green hover:text-rs-light-green underline disabled:opacity-50">
                  {loading ? 'Sending...' : resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend verification email'}
                </button>
                {showSupportLink && (
                  <p className="mt-6 text-sm text-rs-off-white/60">
                    Need help? <a href="mailto:support@resinsmart.ai" className="text-rs-green underline">Contact Support</a>
                  </p>
                )}
              </div>
            </div>
            <div></div>
          </div>
          <div className="art-section flex-1 relative overflow-hidden">
            <img className="art-section-dashboard w-full max-w-8xl rounded-xl border-4 border-rs-jet" src="/dashboard-landing.png" alt="RTi Dashboard Preview" />
            <div className="patterns-container absolute inset-0 pointer-events-none">
              <img src="/patterns/union-green.svg" className="w-2xl absolute top-10 left-10" alt="" />
              <img src="/patterns/union-green.svg" className="w-2xl absolute bottom-10 right-10" alt="" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Signup form
  if (step === 'signup') {
    return (
      <div className="root">
        <div className="auth-root flex w-full h-[100vh]">
          <div className="flex flex-col justify-between items-start h-full w-full max-w-lg px-5 py-10 gap-10 bg-rs-black text-rs-off-white">
            <a href="/">
              <img src="/logos/Primary/White.svg" alt="Resin Smart Logo" className="w-xs" />
            </a>
            <form onSubmit={handleSubmit} className="lg:ml-5 flex flex-col max-w-md w-full" data-hs-ignore="true">
              <h1 className="mb-4 text-3xl font-light">Create Your Account</h1>

              {joinsExistingCompany && domainCheck?.company_name ? (
                <div className="flex items-center gap-3 mb-6 p-3 bg-rs-jet rounded border border-rs-green/30">
                  <Building2 className="text-rs-green flex-shrink-0" size={20} />
                  <div>
                    <div className="text-sm text-rs-green">Joining {domainCheck.company_name}</div>
                    <div className="text-xs text-rs-off-white/50">{form.email}</div>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <span className="text-xl uppercase text-rs-dark-green">60-Day Free Trial</span>
                  <p className="text-xs text-rs-off-white/50 mt-1">{form.email}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <input className="p-3 rounded-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="first_name" placeholder="First name" value={form.first_name} onChange={handleChange} />
                <input className="p-3 rounded-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="last_name" placeholder="Last name" value={form.last_name} onChange={handleChange} />
              </div>

              {!joinsExistingCompany && (
                <input className="mt-3 p-3 rounded-sm w-full bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="company_name" placeholder="Company name" value={form.company_name} onChange={handleChange} required />
              )}

              <RoleFields
                buyingRole={form.buying_role}
                buyingRoleOther={buyingRoleOther}
                jobTitle={form.job_title}
                onChange={handleChange}
                onBuyingRoleOtherChange={setBuyingRoleOther}
              />

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="relative">
                  <input className="p-3 rounded-sm w-full bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="password" type={showPassword ? 'text' : 'password'} placeholder="Password" value={form.password} onChange={handleChange} />
                  <button type="button" className="text-sm absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(s => !s)}>{showPassword ? 'Hide' : 'Show'}</button>
                </div>
                <div className="relative">
                  <input
                    className={`p-3 rounded-sm w-full bg-transparent border ${passwordsMismatch ? 'border-rs-red' : 'border-rs-jet'} text-rs-off-white placeholder-rs-off-white/60 focus:outline-none ${passwordsMismatch ? '' : 'focus:border-rs-green'}`}
                    name="password_confirm" type={showConfirm ? 'text' : 'password'} placeholder="Confirm Password" value={form.password_confirm} onChange={handleChange} aria-invalid={passwordsMismatch}
                  />
                  <button type="button" className="text-sm absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowConfirm(s => !s)}>{showConfirm ? 'Hide' : 'Show'}</button>
                  {passwordsMismatch && <div className="text-rs-red mt-1 text-xs">Passwords do not match</div>}
                </div>
              </div>

              <PasswordStrengthMeter password={form.password} showLabel showFeedback className="mt-2" />

              <div className="mt-3 text-rs-off-white flex items-start justify-start">
                <input type="checkbox" id="agree-terms" checked={agreedToTerms} onChange={(e) => { setAgreedToTerms(e.target.checked); if (e.target.checked) setShowTerms(true); }} className="mt-1 mr-3" />
                <label htmlFor="agree-terms" className="text-sm text-rs-off-white">
                  By clicking this box and using this site, you are agreeing to the ResinSmart.ai{' '}
                  <button type="button" onClick={() => setShowTerms(true)} className="text-rs-green hover:text-rs-light-green underline">terms and conditions</button>
                </label>
              </div>

              {error && <div className="text-rs-red mt-3 text-sm">{error}</div>}

              <button className="mt-4 w-full green text-lg" type="submit" disabled={loading || !agreedToTerms}>
                {loading ? 'Creating your account...' : joinsExistingCompany ? 'Create Account' : 'Start Free Trial'}
              </button>

              <button type="button" className="mt-3 text-sm text-rs-off-white/50 hover:text-rs-off-white underline" onClick={() => { setStep('email'); setError(''); }}>
                Use a different email
              </button>

              {recaptchaSiteKey && (
                <p className="mt-3 text-xs text-rs-off-white/60">
                  This site is protected by reCAPTCHA and the Google
                  <a className="underline ml-1" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                  {' '}and{' '}
                  <a className="underline" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                  {' '}apply.
                </p>
              )}

              <p className="mt-3 text-sm text-rs-off-white/60">Already have an account? <a href="/authorization" className="text-rs-green underline">Sign in</a></p>
            </form>
            <div className="steps"></div>
          </div>
          <div className="art-section flex-1 relative overflow-hidden">
            <img className="art-section-dashboard w-full max-w-8xl rounded-xl border-4 border-rs-jet" src="/dashboard-landing.png" alt="RTi Dashboard Preview" />
            <div className="patterns-container absolute inset-0 pointer-events-none">
              <img src="/patterns/union-green.svg" className="w-2xl absolute top-10 left-10" alt="" />
              <img src="/patterns/union-green.svg" className="w-2xl absolute bottom-10 right-10" alt="" />
            </div>
          </div>
        </div>
        <TermsConditions isOpen={showTerms} onClose={() => setShowTerms(false)} />
      </div>
    );
  }

  // Step 1: Email check
  return (
    <div className="root">
      <div className="auth-root flex w-full h-[100vh]">
        <div className="flex flex-col justify-between items-start h-full w-full max-w-lg px-5 py-10 gap-10 bg-rs-black text-rs-off-white">
          <a href="/">
            <img src="/logos/Primary/White.svg" alt="Resin Smart Logo" className="w-xs" />
          </a>
          <form onSubmit={handleEmailCheck} className="lg:ml-5 flex flex-col max-w-md w-full">
            <h1 className="mb-4 text-3xl font-light">Create Your Account</h1>
            <p className="text-rs-off-white/60 mb-6">
              Enter your company email to get started. If your company already uses ResinSmart, you'll be added to their account automatically.
            </p>

            <input
              className={`p-3 rounded-sm w-full bg-transparent border ${error ? 'border-rs-red' : 'border-rs-jet'} text-rs-off-white placeholder-rs-off-white/60 focus:outline-none ${error ? '' : 'focus:border-rs-green'}`}
              type="email"
              placeholder="Company email address"
              value={emailInput}
              onChange={e => { setEmailInput(e.target.value); setError(''); }}
              autoFocus
            />

            {error && <div className="text-rs-red mt-2 text-sm">{error}</div>}

            <button className="mt-4 w-full green text-lg flex items-center justify-center gap-2" type="submit" disabled={checkingDomain || !emailInput.trim()}>
              {checkingDomain ? 'Checking...' : (<>Continue <ArrowRight size={18} /></>)}
            </button>

            <p className="mt-6 text-sm text-rs-off-white/60">Already have an account? <a href="/authorization" className="text-rs-green underline">Sign in</a></p>
          </form>
          <div className="steps"></div>
        </div>
        <div className="art-section flex-1 relative overflow-hidden">
          <img className="art-section-dashboard w-full max-w-8xl rounded-xl border-4 border-rs-jet" src="/dashboard-landing.png" alt="RTi Dashboard Preview" />
          <div className="patterns-container absolute inset-0 pointer-events-none">
            <img src="/patterns/union-green.svg" className="w-2xl absolute top-10 left-10" alt="" />
            <img src="/patterns/union-green.svg" className="w-2xl absolute bottom-10 right-10" alt="" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
